import { lazy } from 'react'
import { createBrowserRouter } from 'react-router-dom'

import { AppShell } from './AppShell'
import { AuthGuard } from './AuthGuard'
import { AppLayout } from './AppLayout'
import { ProjectMenu } from './layouts/ProjectMenu'
import { ProjectSynthesisLayout } from './layouts/ProjectSynthesisLayout'
import Home from './routes'

const Login = lazy(() => import('./routes/login'))
const Register = lazy(() => import('./routes/register'))
const ForgotPassword = lazy(() => import('./routes/forgot-password'))
const UpdatePassword = lazy(() => import('./routes/update-password'))
const AcceptInvitation = lazy(() => import('./routes/accept-invitation'))
const MagicLink = lazy(() => import('./routes/magic-link'))
const InternalMapRender = lazy(() => import('./routes/internal/map-render'))

const Dashboard = lazy(() => import('./routes/dashboard'))
const Changelog = lazy(() => import('./routes/changelog'))
const Account = lazy(() => import('./routes/account'))

const ClientsList = lazy(() => import('./routes/clients'))
const ClientCreate = lazy(() => import('./routes/clients/create'))
const ClientEdit = lazy(() => import('./routes/clients/edit/[id]'))

const CompaniesList = lazy(() => import('./routes/companies'))
const CompanyCreate = lazy(() => import('./routes/companies/create'))
const CompanyEdit = lazy(() => import('./routes/companies/edit/[id]'))

const SalesmensList = lazy(() => import('./routes/salesmens'))
const SalesmanCreate = lazy(() => import('./routes/salesmens/create'))
const SalesmanEdit = lazy(() => import('./routes/salesmens/edit/[id]'))

const Collaborators = lazy(() => import('./routes/collaborators'))

const Invoices = lazy(() => import('./routes/invoices'))

const ProjectsList = lazy(() => import('./routes/projects'))
const ProjectCreate = lazy(() => import('./routes/projects/create'))
const ProjectsCreating = lazy(() => import('./routes/projects/creating'))
const ProjectsToProcess = lazy(() => import('./routes/projects/to-process'))
const ProjectsToControl = lazy(() => import('./routes/projects/to-control'))
const ProjectsDeposited = lazy(() => import('./routes/projects/deposited'))
const ProjectsCompleted = lazy(() => import('./routes/projects/completed'))
const ProjectsWaitingPayement = lazy(
  () => import('./routes/projects/waiting-payement'),
)
const ProjectsSended = lazy(() => import('./routes/projects/sended'))

const ProjectEdit = lazy(() => import('./routes/projects/edit/[id]'))
const ProjectCerfa = lazy(() => import('./routes/projects/edit/[id]/cerfa'))
const ProjectDeclarers = lazy(
  () => import('./routes/projects/edit/[id]/declarers'),
)
const ProjectExistingBuilding = lazy(
  () => import('./routes/projects/edit/[id]/existing-building'),
)
const ProjectLand = lazy(() => import('./routes/projects/edit/[id]/land'))
const ProjectPool = lazy(() => import('./routes/projects/edit/[id]/pool'))
const ProjectPergola = lazy(() => import('./routes/projects/edit/[id]/pergola'))
const ProjectVeranda = lazy(() => import('./routes/projects/edit/[id]/veranda'))
const ProjectMatrix = lazy(() => import('./routes/projects/edit/[id]/matrix'))
const ProjectInvoices = lazy(
  () => import('./routes/projects/edit/[id]/invoices'),
)
const ProjectEvents = lazy(() => import('./routes/projects/edit/[id]/events'))
const ProjectDocuments = lazy(
  () => import('./routes/projects/edit/[id]/documents'),
)
const ProjectInformations = lazy(
  () => import('./routes/projects/edit/[id]/informations'),
)

const ProjectSynthesisIndex = lazy(
  () => import('./routes/projects/edit/[id]/synthesis'),
)
const ProjectSynthesis0 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/0'),
)
const ProjectSynthesis1 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/1'),
)
const ProjectSynthesis2 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/2'),
)
const ProjectSynthesis3 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/3'),
)
const ProjectSynthesis4 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/4'),
)
const ProjectSynthesis5 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/5'),
)
const ProjectSynthesis6 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/6'),
)
const ProjectSynthesis7 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/7'),
)
const ProjectSynthesis8 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/8'),
)
const ProjectSynthesis9 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/9'),
)
const ProjectSynthesis10 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/10'),
)
const ProjectSynthesis11 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/11'),
)
const ProjectSynthesis12 = lazy(
  () => import('./routes/projects/edit/[id]/synthesis/12'),
)

const UrbanList = lazy(() => import('./routes/urban-analysis'))
const UrbanCreate = lazy(() => import('./routes/urban-analysis/create'))
const UrbanEdit = lazy(() => import('./routes/urban-analysis/edit/[id]'))
const UrbanEditV2 = lazy(() => import('./routes/urban-analysis/edit/[id]/v2'))

const NewRequestChooser = lazy(() => import('./routes/new-request'))

const ToolsIndex = lazy(() => import('./routes/tools'))
const ToolsCadastreDepartments = lazy(
  () => import('./routes/tools/cadastre-departments'),
)
const ToolsColorMaterials = lazy(
  () => import('./routes/tools/color-materials'),
)
const ToolsDefaultProducts = lazy(
  () => import('./routes/tools/default-products'),
)
const ToolsDepositPlatforms = lazy(
  () => import('./routes/tools/deposit-platforms'),
)
const ToolsNotifications = lazy(
  () => import('./routes/tools/notifications'),
)
const ToolsUrbanPlanningData = lazy(
  () => import('./routes/tools/urban-planning-data'),
)
const ToolsUrbanPlanningRules = lazy(
  () => import('./routes/tools/urban-planning-rules'),
)

const CatchAll = lazy(() => import('./routes/_catchAll'))

export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      // Public routes (no auth, no shell)
      { path: '/login', element: <Login /> },
      { path: '/register', element: <Register /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/update-password', element: <UpdatePassword /> },
      { path: '/accept-invitation', element: <AcceptInvitation /> },
      { path: '/magic-link', element: <MagicLink /> },
      { path: '/internal/map-render', element: <InternalMapRender /> },

      // Protected routes (under AuthGuard + AppShell)
      {
        element: (
          <AuthGuard>
            <AppShell />
          </AuthGuard>
        ),
        children: [
          { index: true, element: <Home /> },

          { path: 'dashboard', element: <Dashboard /> },

          { path: 'changelog', element: <Changelog /> },

          { path: 'account', element: <Account /> },

          {
            path: 'clients',
            children: [
              { index: true, element: <ClientsList /> },
              { path: 'create', element: <ClientCreate /> },
              { path: 'edit/:id', element: <ClientEdit /> },
            ],
          },
          {
            path: 'companies',
            children: [
              { index: true, element: <CompaniesList /> },
              { path: 'create', element: <CompanyCreate /> },
              { path: 'edit/:id', element: <CompanyEdit /> },
            ],
          },
          {
            path: 'salesmens',
            children: [
              { index: true, element: <SalesmensList /> },
              { path: 'create', element: <SalesmanCreate /> },
              { path: 'edit/:id', element: <SalesmanEdit /> },
            ],
          },
          { path: 'collaborators', element: <Collaborators /> },
          { path: 'invoices', element: <Invoices /> },

          { path: 'new-request', element: <NewRequestChooser /> },

          {
            path: 'projects',
            children: [
              { index: true, element: <ProjectsList /> },
              { path: 'create', element: <ProjectCreate /> },
              { path: 'creating', element: <ProjectsCreating /> },
              { path: 'to-process', element: <ProjectsToProcess /> },
              { path: 'to-control', element: <ProjectsToControl /> },
              { path: 'deposited', element: <ProjectsDeposited /> },
              { path: 'completed', element: <ProjectsCompleted /> },
              { path: 'waiting-payement', element: <ProjectsWaitingPayement /> },
              { path: 'sended', element: <ProjectsSended /> },
              {
                path: 'edit/:id',
                element: <ProjectMenu />,
                children: [
                  { index: true, element: <ProjectEdit /> },
                  { path: 'cerfa', element: <ProjectCerfa /> },
                  { path: 'declarers', element: <ProjectDeclarers /> },
                  {
                    path: 'existing-building',
                    element: <ProjectExistingBuilding />,
                  },
                  { path: 'land', element: <ProjectLand /> },
                  { path: 'pool', element: <ProjectPool /> },
                  { path: 'pergola', element: <ProjectPergola /> },
                  { path: 'veranda', element: <ProjectVeranda /> },
                  { path: 'matrix', element: <ProjectMatrix /> },
                  { path: 'invoices', element: <ProjectInvoices /> },
                  { path: 'events', element: <ProjectEvents /> },
                  { path: 'documents', element: <ProjectDocuments /> },
                  { path: 'informations', element: <ProjectInformations /> },
                  {
                    path: 'synthesis',
                    element: <ProjectSynthesisLayout />,
                    children: [
                      { index: true, element: <ProjectSynthesisIndex /> },
                      { path: '0', element: <ProjectSynthesis0 /> },
                      { path: '1', element: <ProjectSynthesis1 /> },
                      { path: '2', element: <ProjectSynthesis2 /> },
                      { path: '3', element: <ProjectSynthesis3 /> },
                      { path: '4', element: <ProjectSynthesis4 /> },
                      { path: '5', element: <ProjectSynthesis5 /> },
                      { path: '6', element: <ProjectSynthesis6 /> },
                      { path: '7', element: <ProjectSynthesis7 /> },
                      { path: '8', element: <ProjectSynthesis8 /> },
                      { path: '9', element: <ProjectSynthesis9 /> },
                      { path: '10', element: <ProjectSynthesis10 /> },
                      { path: '11', element: <ProjectSynthesis11 /> },
                      { path: '12', element: <ProjectSynthesis12 /> },
                    ],
                  },
                ],
              },
            ],
          },

          {
            path: 'urban-analysis',
            children: [
              { index: true, element: <UrbanList /> },
              { path: 'create', element: <UrbanCreate /> },
              {
                path: 'edit/:id',
                children: [
                  // V2 is the default interface (issue #634); the legacy V1
                  // panel editor stays reachable as a secondary view. `/v2` is
                  // kept as an alias so existing links and bookmarks still work.
                  { index: true, element: <UrbanEditV2 /> },
                  { path: 'v1', element: <UrbanEdit /> },
                  { path: 'v2', element: <UrbanEditV2 /> },
                ],
              },
            ],
          },

          {
            path: 'tools',
            children: [
              { index: true, element: <ToolsIndex /> },
              {
                path: 'cadastre-departments',
                element: <ToolsCadastreDepartments />,
              },
              {
                path: 'color-materials',
                element: <ToolsColorMaterials />,
              },
              {
                path: 'default-products',
                element: <ToolsDefaultProducts />,
              },
              {
                path: 'deposit-platforms',
                element: <ToolsDepositPlatforms />,
              },
              {
                path: 'notifications',
                element: <ToolsNotifications />,
              },
              {
                path: 'urban-planning-data',
                element: <ToolsUrbanPlanningData />,
              },
              {
                path: 'urban-planning-rules',
                element: <ToolsUrbanPlanningRules />,
              },
            ],
          },

          { path: '*', element: <CatchAll /> },
        ],
      },
    ],
  },
])
